/**
 * Un seul point de passage pour les appels à l'API.
 *
 * Il existe pour une raison unique mais suffisante : **une session peut expirer entre
 * deux clics**. Douze heures, une révocation par un administrateur, un redémarrage — et
 * l'appel suivant revient en 401. Si chaque écran traitait ce cas lui-même, l'un d'eux
 * l'oublierait, et l'utilisateur verrait « L'API a répondu 401 » au milieu d'un tableau
 * de mesures, sans comprendre qu'il doit simplement se reconnecter.
 *
 * Ici, tout 401 est signalé à un seul auditeur — le fournisseur de session — qui repasse
 * l'application derrière l'écran de connexion. Les écrans n'ont rien à savoir.
 *
 * `credentials: "same-origin"` est explicite plutôt que laissé au défaut : le cookie
 * porte le préfixe `__Host-`, il n'accompagne que des requêtes de même origine. L'écrire
 * évite qu'un futur déplacement de l'API vers un autre domaine échoue silencieusement,
 * sans cookie et sans message.
 */

type Listener = () => void;

let listener: Listener | null = null;

/** Enregistre l'unique auditeur des pertes de session. Retourne son retrait. */
export function onSessionLost(fn: Listener): () => void {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

/**
 * `fetch` avec la gestion du 401 en place.
 *
 * La réponse est renvoyée telle quelle : l'appelant garde la main sur ses propres codes
 * d'erreur (409 pour une analyse expirée, 403 pour un droit manquant). Seule la perte de
 * session est traitée ici, parce qu'elle seule concerne toute l'application.
 */
export async function apiFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const response = await fetch(input, {
    credentials: "same-origin",
    ...init,
  });

  if (response.status === 401) listener?.();
  return response;
}

/** Extrait le message du serveur, ou en fabrique un lisible. */
export async function detailOf(
  response: Response,
  fallback?: string,
): Promise<string> {
  const payload = (await response.json().catch(() => null)) as {
    detail?: string;
  } | null;
  return (
    payload?.detail ?? fallback ?? `L'API a répondu ${String(response.status)}`
  );
}
